import { twMerge } from "tailwind-merge";
import { BooleanText, Nullable } from "../../api/types";
import { DataInputFormField, FormField } from "../../types";
import { isRPEFormField } from "../../utils";
import Loader from "../AppLoader";
import LabeledInput from "../forms/LabeledInput";

export type DataInputGenericFormProps<T> = {
    formTitle?: string;
    formFields: Array<FormField>;
    formData: Nullable<T>;
    onFromValuesChange: (
        data: {
            value: string | number | BooleanText | null;
            fieldSettings: DataInputFormField;
        },
        inputLevel?: "primary" | "secondary"
    ) => void;
    loading: boolean;
    headers?: string[];
    dense?: boolean;
    isHorizontal?: boolean;
    classes?: {
        root?: string;
        title?: string;
        table?: string;
    };
};

export default function DataInputGenericForm<T>({
    formTitle,
    formFields,
    formData,
    onFromValuesChange,
    loading,
    headers,
    dense,
    isHorizontal,
    classes = {},
}: DataInputGenericFormProps<T>) {
    return (
        <div className={twMerge(classes.root)}>
            {formTitle ? (
                <h5
                    className={twMerge(
                        `font-bold text-sm xl:text-base`,
                        classes.title
                    )}
                >
                    {formTitle}
                </h5>
            ) : null}
            <form
                onSubmit={(e) => e.preventDefault()}
                className={twMerge(
                    `flex`,
                    dense ? "gap-1.5 pt-2 pb-2" : "gap-7 xl:gap-8 py-4 xl:py-5",
                    `mt-1 rounded-md bg-white shadow-[0px_2px_8px_0px_#366CD640] pl-2.5 xl:pl-6 pr-1 xl:pr-2`,
                    !isHorizontal
                        ? [
                              "w-64 xl:w-[366px] flex-col",
                              !dense && "min-h-[86px]",
                          ]
                        : "grid",
                    classes.table
                )}
                style={{
                    gridTemplateColumns: isHorizontal
                        ? `repeat(${formFields.length}, 1fr)`
                        : undefined,
                }}
            >
                {headers ? <Headers headers={headers} /> : null}
                {loading ? (
                    <div className="w-full h-full grid place-content-center">
                        <Loader />
                    </div>
                ) : (
                    formData &&
                    formFields.map((field) => {
                        return isRPEFormField(field) ? (
                            <div className={"flex"} key={field.id}>
                                <LabeledInput
                                    key={field.scale.id}
                                    dataInputFormProps={field.scale}
                                    onChange={(value) => {
                                        onFromValuesChange({
                                            value,
                                            fieldSettings: field.scale,
                                        });
                                    }}
                                    withoutTooltip
                                    isSmallLabel
                                    className="w-[100px]"
                                />
                                <LabeledInput
                                    key={field.value.id}
                                    dataInputFormProps={field.value}
                                    onChange={(value) => {
                                        onFromValuesChange({
                                            value,
                                            fieldSettings: field.value,
                                        });
                                    }}
                                    withoutLabel
                                />
                            </div>
                        ) : (
                            <LabeledInput
                                key={field.id}
                                dataInputFormProps={field}
                                onChange={(value) => {
                                    onFromValuesChange({
                                        value,
                                        fieldSettings: field,
                                    });
                                }}
                                onSecondaryChange={(value) => {
                                    onFromValuesChange(
                                        {
                                            value,
                                            fieldSettings: field,
                                        },
                                        "secondary"
                                    );
                                }}
                            />
                        );
                    })
                )}
            </form>
        </div>
    );
}

const Headers = ({ headers }: { headers: string[] }) => {
    const isTwoHeaders = headers.length === 2;
    return (
        <div
            className={twMerge(
                `pt-2 xl:w-[96%] text-xs xl:text-base font-semibold border-b xl:border-b-2 border-black flex gap-2 xl:gap-4 h-6 xl:h-[38px] items-center justify-between`,
                isTwoHeaders && "justify-between"
            )}
        >
            <div className={`shrink-0 h-full xl:w-[102px]`}>{headers[0]}</div>
            <div
                className={`text-center ${
                    isTwoHeaders ? "" : "min-w-[130px] xl:w-[204px]"
                } xl:gap-2 h-full flex ml-2 justify-between`}
            >
                <div
                    className={`shrink-0 ${
                        isTwoHeaders ? "mr-7" : "w-[40px] xl:w-[70px]"
                    }`}
                >
                    {headers[1]}
                </div>
                {headers[2] ? (
                    <div className={"w-full"}>{headers[2]}</div>
                ) : null}
            </div>
        </div>
    );
};
